/*
 * Pickup availability (Hyper v1.5.0 + ODP Delivery & collection panel)
 *
 * What changed from stock v1.5.0, and why:
 * - The first paint is Liquid (snippets/odp-fulfilment-panel.liquid), so nothing is fetched
 *   on page load. v1.5 fetched the section on every product page view.
 * - The store-details drawer is fetched only when a shopper opens it, then reused while
 *   the variant stays the same.
 * - The drawer gets the product section's own id (data-drawer-id), so the main product and
 *   quick view no longer remove each other's drawer.
 * - The offsite dispatch range is worked out here, from today, skipping weekends and the
 *   holidays the panel lists, so a cached page never shows a stale date.
 * Kept from v1.5.0: the abort + de-duplication of product-info's two update() calls per
 * variant change, and the error template taken before any early return.
 */
if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        // Taken first: update() can empty the element, and a later fetch can still fail.
        const template = this.querySelector('template');
        this.errorHtml = template ? template.content.firstElementChild.cloneNode(true) : null;

        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onTriggerIntent = this.onTriggerIntent.bind(this);

        // The Liquid first paint already shows this variant: product-info's first update() for
        // it must not fetch it again.
        this.requestedVariantId = this.dataset.variantId || null;
        this.renderedVariantId = this.dataset.variantId || null;
      }

      connectedCallback() {
        // Capture phase on the wrapper: runs before the drawer's own listener on the button,
        // so the lazy fetch decides when the drawer opens (see openDrawer).
        this.addEventListener('click', this.onTriggerClick, true);
        // Hover/focus intent starts the drawer fetch, so it's usually there by the click.
        this.addEventListener('pointerover', this.onTriggerIntent);
        this.addEventListener('focusin', this.onTriggerIntent);
        PickupAvailability.hydrateDates(this);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onTriggerClick, true);
        this.removeEventListener('pointerover', this.onTriggerIntent);
        this.removeEventListener('focusin', this.onTriggerIntent);
        this.abortController?.abort();
        this.drawerAbortController?.abort();
      }

      get drawerId() {
        return this.dataset.drawerId || 'PickupDrawer';
      }

      get drawer() {
        return document.getElementById(this.drawerId);
      }

      buildUrl(variantId) {
        let rootUrl = this.dataset.rootUrl || '/';
        if (!rootUrl.endsWith('/')) rootUrl += '/';
        return `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;
      }

      fetchSection(variantId, signal) {
        return fetch(this.buildUrl(variantId), { signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Pickup availability: HTTP ${response.status}`);
            return response.text();
          })
          .then((text) => new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section'));
      }

      // ─── Variant changes (called by product-info.js) ─────────────

      update(variant) {
        if (variant?.available) {
          // Already requested (product-info's optimistic update, or the Liquid first paint).
          if (String(variant.id) === this.requestedVariantId) return;
          this.fetchAvailability(variant.id);
        } else {
          this.abortController?.abort();
          this.requestedVariantId = null;
          this.renderedVariantId = null;
          this.removeAttribute('available');
          this.clearContent();
          this.removeDrawer();
        }
      }

      fetchAvailability(variantId) {
        // Only the latest request may render, or an older variant's stores could land last.
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.requestedVariantId = String(variantId);

        this.fetchSection(variantId, this.abortController.signal)
          .then((section) => this.renderPreview(section, String(variantId)))
          .catch((error) => {
            if (error.name === 'AbortError') return;
            this.renderError();
          });
      }

      onClickRefreshList() {
        const variantId = this.requestedVariantId || this.dataset.variantId;
        this.requestedVariantId = null;
        this.fetchAvailability(variantId);
      }

      // ─── Render ──────────────────────────────────────────────────

      clearContent() {
        Array.from(this.children).forEach((child) => {
          if (child.tagName !== 'TEMPLATE') child.remove();
        });
      }

      renderPreview(section, variantId) {
        const panel = section?.querySelector('.odp-fulfil');

        if (!panel) {
          this.clearContent();
          this.removeAttribute('available');
          this.renderedVariantId = null;
          this.removeDrawer();
          return;
        }

        this.clearContent();
        this.prepend(panel);
        this.setAttribute('available', '');
        this.renderedVariantId = variantId;
        this.retargetTriggers(this);
        PickupAvailability.hydrateDates(this);

        // This response carries the drawer too: attach it now rather than fetch it again later.
        this.attachDrawer(section, variantId);
      }

      renderError() {
        this.clearContent();
        if (!this.errorHtml) return;

        const error = this.errorHtml.cloneNode(true);
        this.prepend(error);
        error.querySelector('button')?.addEventListener('click', this.onClickRefreshList);
      }

      // ─── Drawer (lazy) ───────────────────────────────────────────

      onTriggerClick(event) {
        const trigger = event.target.closest('[data-pickup-drawer-trigger]');
        if (!trigger || !this.contains(trigger)) return;

        event.preventDefault();
        event.stopPropagation();
        this.openDrawer(trigger);
      }

      onTriggerIntent(event) {
        if (event.target.closest?.('[data-pickup-drawer-trigger]')) this.loadDrawer();
      }

      // Resolves with this instance's drawer for the variant on show, fetching it at most once.
      loadDrawer() {
        const variantId = String(this.renderedVariantId || this.dataset.variantId);
        const existing = this.drawer;
        if (existing && existing.dataset.variantId === variantId) return Promise.resolve(existing);
        if (this.drawerRequest?.variantId === variantId) return this.drawerRequest.promise;

        this.drawerAbortController?.abort();
        this.drawerAbortController = new AbortController();
        const promise = this.fetchSection(variantId, this.drawerAbortController.signal)
          .then((section) => {
            this.attachDrawer(section, variantId);
            return this.drawer;
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
            return null;
          })
          .finally(() => {
            if (this.drawerRequest?.promise === promise) this.drawerRequest = null;
          });
        this.drawerRequest = { variantId, promise };
        return promise;
      }

      openDrawer(trigger) {
        const existing = this.drawer;
        const variantId = String(this.renderedVariantId || this.dataset.variantId);
        if (existing && existing.dataset.variantId === variantId) {
          existing.show(trigger);
          return Promise.resolve(existing);
        }

        trigger.setAttribute('aria-busy', 'true');
        return this.loadDrawer()
          .then((drawer) => {
            if (drawer) drawer.show(trigger);
            return drawer;
          })
          .finally(() => trigger.removeAttribute('aria-busy'));
      }

      attachDrawer(section, variantId) {
        const drawer = section?.querySelector('pickup-availability-drawer');
        if (!drawer) return;

        this.removeDrawer();

        const sourceId = drawer.id;
        drawer.id = this.drawerId;
        drawer.dataset.variantId = String(variantId);

        // Ids inside it follow the drawer's (title, close button, overlay).
        drawer.querySelectorAll(`[aria-controls="${sourceId}"]`).forEach((element) => {
          element.setAttribute('aria-controls', this.drawerId);
        });
        const title = drawer.querySelector('[data-drawer-title]');
        if (title) {
          title.id = `${this.drawerId}-title`;
          drawer.setAttribute('aria-labelledby', title.id);
        }

        document.body.appendChild(drawer);
      }

      removeDrawer() {
        this.drawerAbortController?.abort();
        this.drawerRequest = null;
        this.drawer?.remove();
      }

      retargetTriggers(root) {
        root.querySelectorAll('[data-pickup-drawer-trigger]').forEach((button) => {
          button.setAttribute('aria-controls', this.drawerId);
        });
        // ODP online exclusive: the gallery tag links to this row by the product section's id.
        root.querySelectorAll('[data-odp-exclusive-row]').forEach((row) => {
          row.id = this.drawerId.replace('PickupDrawer', 'OdpExclusive');
        });
      }

      // ─── Dispatch dates ──────────────────────────────────────────

      static toISODate(date) {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${date.getFullYear()}-${month}-${day}`;
      }

      static addBusinessDays(start, days, holidays = []) {
        const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        let added = 0;
        while (added < days) {
          date.setDate(date.getDate() + 1);
          const weekday = date.getDay();
          if (weekday === 0 || weekday === 6) continue;
          if (holidays.includes(PickupAvailability.toISODate(date))) continue;
          added += 1;
        }
        return date;
      }

      static formatDay(date, locale, withMonth) {
        // English (ODP's only storefront language): plain arrays. The first Intl.DateTimeFormat
        // call costs ~80ms on a 4x-throttled phone (ICU data load) and showed up as a long task.
        if (/^en\b/i.test(locale)) {
          const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
          const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()];
          return withMonth ? `${weekday} ${date.getDate()} ${month}` : `${weekday} ${date.getDate()}`;
        }

        const options = { weekday: 'short', day: 'numeric' };
        if (withMonth) options.month = 'short';
        const parts = new Intl.DateTimeFormat(locale, options).formatToParts(date);
        const pick = (type) => parts.find((part) => part.type === type)?.value || '';
        // "Mon 19 Oct", whatever separators the locale uses. The day number comes from the date
        // itself: en-ZA pads it ("03 Nov") in some option combinations.
        return [pick('weekday'), String(date.getDate()), withMonth ? pick('month') : ''].filter(Boolean).join(' ');
      }

      static hydrateDates(root, now = new Date()) {
        root.querySelectorAll('[data-dispatch-range]').forEach((element) => {
          const min = parseInt(element.dataset.minDays, 10);
          const max = parseInt(element.dataset.maxDays, 10);
          const template = element.dataset.template;
          if (Number.isNaN(min) || Number.isNaN(max) || !template) return;

          const holidays = (element.dataset.holidays || '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean);
          const locale = document.documentElement.lang || 'en-ZA';
          const from = PickupAvailability.addBusinessDays(now, min, holidays);
          const to = PickupAvailability.addBusinessDays(now, max, holidays);
          const sameMonth = from.getMonth() === to.getMonth();

          element.textContent = template
            .replace('[from]', PickupAvailability.formatDay(from, locale, !sameMonth))
            .replace('[to]', PickupAvailability.formatDay(to, locale, true));
          element.dataset.hydrated = 'true';
        });
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends BasicModal {
      constructor() {
        super();
      }
    }
  );
}